import { useState } from "react";
import axios from "axios";
import { X, Buildings, User, Phone, Envelope, MapPin, CheckCircle, Spinner } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["EndUser", "Dealer", "Distributor", "OEM", "Service Center", "Other"];

/**
 * AddCustomerModal
 * ----------------
 * Single-form Customer + primary Person creation. Used by:
 *  • Staff (admin / sales-rep) on /customers-us / /customers-ca to create a
 *    new customer record from scratch — `selfService=false`.
 *  • Customers on /my-account when they don't have a profile yet —
 *    `selfService=true` so the backend stamps the Customer row with the
 *    self-service flag + audit tag in `_Notes`.
 *
 * Props:
 *   user         — current logged-in user (carries email)
 *   country      — "US" / "CA" — pre-fills the country picker
 *   isCA         — brand tint
 *   selfService  — true when called from a customer-side context
 *   onClose      — fired on cancel / escape
 *   onCreated    — fired with `{customer_id, pfcid}` once the row is committed
 */
export default function AddCustomerModal({ user, country = "US", isCA = false, selfService = false, onClose, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company_name: "", type: "EndUser",
    country: country.toUpperCase(),
    currency: country.toUpperCase() === "CA" ? "CAD" : "USD",
    bill_to_address: "",
    notes: "",
    contact_name: selfService ? (user?.name || "") : "",
    contact_email: selfService ? (user?.email || "") : "",
    contact_phone: "",
    contact_phone_alt: "",
    contact_extension: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company_name.trim()) return setError("Company Name is required");
    if (!form.contact_name.trim()) return setError("Primary contact name is required");
    if (!form.contact_email.trim() && !form.contact_phone.trim()) {
      return setError("Provide an email or phone for the primary contact");
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/customers`, {
        company_name: form.company_name.trim(),
        type: form.type,
        bill_to_address: form.bill_to_address.trim(),
        country: form.country,
        currency: form.currency,
        notes: form.notes.trim(),
        self_service: !!selfService,
        primary_contact: {
          name: form.contact_name.trim(),
          email: form.contact_email.trim(),
          phone: form.contact_phone.trim(),
          telephone_alternate: form.contact_phone_alt.trim(),
          extension: form.contact_extension.trim(),
        },
      }, {
        withCredentials: true,
        headers: user?.email ? { "X-User-Email": user.email } : {},
      });
      onCreated && onCreated(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to create customer";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const tint = isCA ? "border-red-200 bg-red-50/40" : "border-blue-200 bg-blue-50/40";
  const focusRing = isCA ? "focus:ring-red-300 focus:border-red-400" : "focus:ring-blue-300 focus:border-blue-400";
  const accentBtn = isCA ? "bg-red-700 hover:bg-red-800" : "bg-blue-700 hover:bg-blue-800";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="add-customer-modal">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className={`flex items-start justify-between gap-3 p-4 border-b ${isCA ? "border-red-100" : "border-blue-100"}`}>
          <div>
            <div className="flex items-center gap-2">
              <Buildings size={16} className={isCA ? "text-red-600" : "text-blue-700"} />
              <h2 className="text-lg font-black tracking-tight font-['Chivo']">
                {selfService ? "Create my customer profile" : "Add new customer"}
              </h2>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {selfService
                ? "We'll link this profile to your account so your future quotes, files & notes are saved here."
                : "Fill the basics — you can always add more details on the customer record after."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="add-customer-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Company section */}
          <Section title="Company">
            <Field label="Company name" required>
              <input
                value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                placeholder="e.g. Acme Manufacturing"
                className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`}
                data-testid="add-customer-company-name" required maxLength={200} autoFocus
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select value={form.type} onChange={(e) => set("type", e.target.value)} className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`} data-testid="add-customer-type">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Compressor owner?">
                <p className="text-[11px] text-slate-500 italic h-9 flex items-center" data-testid="add-customer-compressor-owner-note">
                  Auto-set when a compressor is registered to this customer
                </p>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Country">
                <select value={form.country} onChange={(e) => {
                  const v = e.target.value;
                  set("country", v);
                  set("currency", v === "CA" ? "CAD" : "USD");
                }} className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`} data-testid="add-customer-country">
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="MX">Mexico</option>
                  <option value="OT">Other</option>
                </select>
              </Field>
              <Field label="Currency">
                <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`} data-testid="add-customer-currency">
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                  <option value="EUR">EUR</option>
                </select>
              </Field>
            </div>
            <Field label="Bill-to address">
              <textarea
                value={form.bill_to_address} onChange={(e) => set("bill_to_address", e.target.value)}
                placeholder="Street, City, State / Province, Postal Code"
                className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1 min-h-[60px] resize-none`}
                data-testid="add-customer-bill-address" rows={2} maxLength={500}
              />
            </Field>
          </Section>

          {/* Primary contact */}
          <Section title="Primary contact">
            <Field label="Full name" required>
              <input
                value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                placeholder="First Last"
                className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`}
                data-testid="add-customer-contact-name" required maxLength={120}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <input
                  type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)}
                  placeholder="contact@company.com"
                  className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`}
                  data-testid="add-customer-contact-email" maxLength={200}
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)}
                  placeholder="+1 555-555-1234"
                  className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`}
                  data-testid="add-customer-contact-phone" maxLength={80}
                />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Alt phone" colSpan={2}>
                <input
                  type="tel" value={form.contact_phone_alt} onChange={(e) => set("contact_phone_alt", e.target.value)}
                  placeholder="optional"
                  className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`}
                  data-testid="add-customer-contact-phone-alt" maxLength={80}
                />
              </Field>
              <Field label="Ext.">
                <input
                  value={form.contact_extension} onChange={(e) => set("contact_extension", e.target.value)}
                  placeholder="ext"
                  className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1`}
                  data-testid="add-customer-contact-ext" maxLength={8}
                />
              </Field>
            </div>
          </Section>

          {/* Notes */}
          <Section title="Notes (optional)">
            <textarea
              value={form.notes} onChange={(e) => set("notes", e.target.value)}
              placeholder="Anything we should remember about this customer?"
              className={`w-full text-sm p-2 rounded-sm border border-slate-200 outline-none ${focusRing} focus:ring-1 min-h-[60px] resize-none`}
              data-testid="add-customer-notes" rows={2} maxLength={1500}
            />
          </Section>

          {selfService && (
            <p className={`text-[11px] p-2 rounded-sm border ${tint} text-slate-600`} data-testid="add-customer-self-service-banner">
              <CheckCircle size={11} weight="bold" className="inline -mt-0.5 mr-1" />
              Self-service: any files or notes you upload after this will be tagged so only you and your US Air rep can see them.
            </p>
          )}

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="add-customer-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-2 p-3 border-t ${isCA ? "border-red-100 bg-red-50/30" : "border-blue-100 bg-blue-50/30"}`}>
          <button
            type="button" onClick={onClose}
            className="text-xs font-bold uppercase tracking-wider px-3 h-8 rounded-sm border border-slate-200 text-slate-600 hover:bg-white"
            data-testid="add-customer-cancel" disabled={submitting}
          >Cancel</button>
          <button
            type="submit"
            disabled={submitting || !form.company_name.trim() || !form.contact_name.trim()}
            data-testid="add-customer-submit"
            className={`text-xs font-bold uppercase tracking-wider px-3 h-8 rounded-sm text-white ${submitting ? "bg-slate-400" : accentBtn} disabled:opacity-60`}
          >
            {submitting ? <span className="inline-flex items-center gap-1"><Spinner size={11} className="animate-spin" /> Saving…</span> : "Create customer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{title}</h4>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
function Field({ label, required, children, colSpan }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5 block">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
    </div>
  );
}
